import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
    Mail,
    Phone,
    Star,
    X,
    Upload,
    Download,
    UserPlus,
    Search,
    Building2,
    Briefcase,
    FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn, getInitials } from "@/lib/utils";
import { useContacts, useCreateContact, useUpdateContact, useDeleteContact, useToggleFavoriteContact } from "@/api/hooks";
import { mailApi, type Contact, type UpsertContactPayload } from "@/api/mailApi";
import { useAccessToken } from "@/auth/useAccessToken";

const EMPTY_FORM: UpsertContactPayload = {
    displayName: "",
    emailAddress: "",
    phone: "",
    company: "",
    jobTitle: "",
    notes: "",
    isFavorite: false
};

export function PeoplePage() {
    const token = useAccessToken();
    const qc = useQueryClient();
    const { data: contacts = [], isPending, isError, error } = useContacts();

    const createContact = useCreateContact();
    const updateContact = useUpdateContact();
    const deleteContact = useDeleteContact();
    const toggleFavorite = useToggleFavoriteContact();

    const [search, setSearch] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editContact, setEditContact] = useState<Contact | null>(null);
    const [form, setForm] = useState<UpsertContactPayload>(EMPTY_FORM);
    const [deleteConfirm, setDeleteConfirm] = useState<Contact | null>(null);
    const [exportError, setExportError] = useState<string | null>(null);
    const [importError, setImportError] = useState<string | null>(null);
    const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null);
    const importRef = useRef<HTMLInputElement>(null);

    // Filter client-side
    const filtered = search.trim()
        ? contacts.filter((c) => {
              const q = search.toLowerCase();
              return (
                  c.displayName.toLowerCase().includes(q) ||
                  c.emailAddress.toLowerCase().includes(q) ||
                  (c.company ?? "").toLowerCase().includes(q) ||
                  (c.jobTitle ?? "").toLowerCase().includes(q)
              );
          })
        : contacts;

    // Sort: favorites first, then alpha
    const sorted = [...filtered].sort((a, b) => {
        if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
        return a.displayName.localeCompare(b.displayName);
    });

    const openCreate = () => {
        setEditContact(null);
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };

    const openEdit = (c: Contact) => {
        setEditContact(c);
        setForm({
            displayName: c.displayName,
            emailAddress: c.emailAddress,
            phone: c.phone ?? "",
            company: c.company ?? "",
            jobTitle: c.jobTitle ?? "",
            notes: c.notes ?? "",
            isFavorite: c.isFavorite
        });
        setDialogOpen(true);
    };

    const handleSave = () => {
        const payload: UpsertContactPayload = {
            ...form,
            phone: form.phone || null,
            company: form.company || null,
            jobTitle: form.jobTitle || null,
            notes: form.notes || null
        };
        if (editContact) {
            updateContact.mutate(
                { id: editContact.id, payload },
                { onSuccess: () => setDialogOpen(false) }
            );
        } else {
            createContact.mutate(payload, { onSuccess: () => setDialogOpen(false) });
        }
    };

    const handleDelete = (c: Contact) => {
        deleteContact.mutate(c.id, { onSuccess: () => setDeleteConfirm(null) });
    };

    const handleToggleFavorite = (c: Contact) => {
        toggleFavorite.mutate({
            id: c.id,
            payload: {
                displayName: c.displayName,
                emailAddress: c.emailAddress,
                phone: c.phone,
                company: c.company,
                jobTitle: c.jobTitle,
                notes: c.notes,
                isFavorite: !c.isFavorite
            }
        });
    };

    const handleExport = async (format: "csv" | "vcf") => {
        setExportError(null);
        try {
            await mailApi.exportContacts(format, token);
        } catch (err) {
            setExportError(err instanceof Error ? err.message : String(err));
        }
    };

    const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImportError(null);
        setImportResult(null);
        try {
            const result = await mailApi.importContacts(file, token);
            setImportResult(result);
            void qc.invalidateQueries({ queryKey: ["contacts"] });
        } catch (err) {
            setImportError(err instanceof Error ? err.message : String(err));
        } finally {
            if (importRef.current) importRef.current.value = "";
        }
    };

    const saving = createContact.isPending || updateContact.isPending;
    const saveError = createContact.error ?? updateContact.error;

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-6">
                <h1 className="text-lg font-semibold">People</h1>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search contacts…"
                            className="h-8 w-48 pl-7 text-sm"
                        />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => void handleExport("csv")} title="Export CSV">
                        <Download className="h-4 w-4" />
                        CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => void handleExport("vcf")} title="Export vCard">
                        <FileText className="h-4 w-4" />
                        vCard
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => importRef.current?.click()} title="Import">
                        <Upload className="h-4 w-4" />
                        Import
                    </Button>
                    <input
                        ref={importRef}
                        type="file"
                        accept=".csv,.vcf"
                        className="hidden"
                        onChange={(e) => void handleImportFile(e)}
                    />
                    <Button size="sm" onClick={openCreate}>
                        <UserPlus className="h-4 w-4" />
                        Add contact
                    </Button>
                </div>
            </header>

            {exportError ? (
                <div className="border-b bg-destructive/10 px-6 py-1.5 text-xs text-destructive">{exportError}</div>
            ) : null}
            {importError ? (
                <div className="border-b bg-destructive/10 px-6 py-1.5 text-xs text-destructive">{importError}</div>
            ) : null}
            {importResult ? (
                <div className="border-b bg-primary/5 px-6 py-1.5 text-xs text-primary">
                    Imported {importResult.imported} contact{importResult.imported !== 1 ? "s" : ""}
                    {importResult.skipped > 0 ? `, ${importResult.skipped} skipped` : ""}.
                </div>
            ) : null}

            <ScrollArea className="flex-1">
                {isPending ? (
                    <div className="px-6 py-8 text-center text-sm text-muted-foreground">Loading contacts…</div>
                ) : isError ? (
                    <div className="px-6 py-8 text-center text-sm text-destructive">
                        {error instanceof Error ? error.message : "Could not load contacts."}
                    </div>
                ) : sorted.length === 0 ? (
                    <div className="px-6 py-8 text-center text-sm text-muted-foreground">
                        {search ? "No contacts match your search." : "No contacts yet — add one to get started."}
                    </div>
                ) : (
                    <ul role="list" className="divide-y">
                        {sorted.map((c) => (
                            <li
                                key={c.id}
                                className="flex cursor-pointer items-center gap-4 px-6 py-3 hover:bg-accent/40"
                                onClick={() => openEdit(c)}
                            >
                                <Avatar className="h-10 w-10">
                                    <AvatarFallback>{getInitials(c.displayName || c.emailAddress)}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 font-medium">
                                        {c.displayName}
                                        {c.isFavorite ? (
                                            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                                        ) : null}
                                    </div>
                                    {c.jobTitle || c.company ? (
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            {c.jobTitle ? (
                                                <>
                                                    <Briefcase className="h-3 w-3" />
                                                    <span>{c.jobTitle}</span>
                                                </>
                                            ) : null}
                                            {c.jobTitle && c.company ? <span>·</span> : null}
                                            {c.company ? (
                                                <>
                                                    <Building2 className="h-3 w-3" />
                                                    <span>{c.company}</span>
                                                </>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="flex flex-col items-end gap-0.5 text-xs">
                                    <a
                                        href={`mailto:${c.emailAddress}`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="inline-flex items-center gap-1 text-primary hover:underline"
                                    >
                                        <Mail className="h-3 w-3" />
                                        {c.emailAddress}
                                    </a>
                                    {c.phone ? (
                                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                                            <Phone className="h-3 w-3" />
                                            {c.phone}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="flex shrink-0 gap-1" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        onClick={() => handleToggleFavorite(c)}
                                        aria-label={c.isFavorite ? "Remove from favorites" : "Add to favorites"}
                                        title={c.isFavorite ? "Remove from favorites" : "Add to favorites"}
                                        className={cn(
                                            "rounded p-1 transition-colors",
                                            c.isFavorite
                                                ? "text-amber-400 hover:text-amber-500"
                                                : "text-muted-foreground hover:text-foreground"
                                        )}
                                    >
                                        <Star className={cn("h-4 w-4", c.isFavorite && "fill-amber-400")} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDeleteConfirm(c)}
                                        aria-label={`Delete ${c.displayName}`}
                                        title="Delete contact"
                                        className="rounded p-1 text-muted-foreground hover:text-destructive"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </ScrollArea>

            {/* Create / Edit dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editContact ? "Edit contact" : "New contact"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                Display name <span className="text-destructive">*</span>
                            </label>
                            <Input
                                value={form.displayName}
                                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                                placeholder="Full name"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                Email address <span className="text-destructive">*</span>
                            </label>
                            <Input
                                type="email"
                                value={form.emailAddress}
                                onChange={(e) => setForm((f) => ({ ...f, emailAddress: e.target.value }))}
                                placeholder="email@example.com"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-muted-foreground">Phone</label>
                            <Input
                                value={form.phone ?? ""}
                                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                                placeholder="+1 555 000 1234"
                            />
                        </div>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label className="mb-1 block text-xs font-medium text-muted-foreground">Company</label>
                                <Input
                                    value={form.company ?? ""}
                                    onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                                    placeholder="Acme Corp"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="mb-1 block text-xs font-medium text-muted-foreground">Job title</label>
                                <Input
                                    value={form.jobTitle ?? ""}
                                    onChange={(e) => setForm((f) => ({ ...f, jobTitle: e.target.value }))}
                                    placeholder="Engineer"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="mb-1 block text-xs font-medium text-muted-foreground">Notes</label>
                            <textarea
                                value={form.notes ?? ""}
                                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                                placeholder="Any notes…"
                                rows={3}
                                className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                id="favorite-toggle"
                                type="checkbox"
                                checked={!!form.isFavorite}
                                onChange={(e) => setForm((f) => ({ ...f, isFavorite: e.target.checked }))}
                                className="h-4 w-4 accent-primary"
                            />
                            <label htmlFor="favorite-toggle" className="cursor-pointer text-sm">
                                Mark as favorite
                            </label>
                        </div>
                        {saveError ? (
                            <p className="text-xs text-destructive">
                                {saveError instanceof Error ? saveError.message : String(saveError)}
                            </p>
                        ) : null}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving || !form.displayName.trim() || !form.emailAddress.trim()}
                        >
                            {saving ? "Saving…" : editContact ? "Save changes" : "Add contact"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteConfirm !== null} onOpenChange={() => setDeleteConfirm(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Delete contact?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        Are you sure you want to delete{" "}
                        <span className="font-semibold">{deleteConfirm?.displayName}</span>? This cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
                            disabled={deleteContact.isPending}
                        >
                            {deleteContact.isPending ? "Deleting…" : "Delete"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
